import React, { useState } from 'react';
import { NavLink } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  Clock,
  Calendar,
  CheckSquare,
  CalendarDays,
  X,
  UserPlus,
  ClipboardCheck,
  Wallet,
  BarChart3,
  Star,
  ChevronDown,
  ChevronsLeft,
  FileText,
  Briefcase,
  Home as HomeIcon,
  Hourglass,
  WalletCards,
  Gift,
  GraduationCap,
  ScrollText
} from 'lucide-react';
import { Cake } from 'lucide-react'; 

const Sidebar = ({ isOpen, onClose, counts }) => {
  const [openSections, setOpenSections] = useState({
    'Employee Management': true,
    'Performance Review': true,
    'Payroll Management': true,
  });
  const [collapsed, setCollapsed] = useState(false);

  const attendancePending = counts?.attendancePending ?? null;
  const leavePending = counts?.leavePending ?? null;
  const taskPending = counts?.taskPending ?? null;
  const onboardingPending = counts?.onboardingPending ?? null;
  const offerLetterPending = counts?.offerLetterPending ?? null;
  const wfhPending = counts?.wfhPending ?? null;
  const compOffPending = counts?.compOffPending ?? null;
  const probationPending = counts?.probationPending ?? null;
  const internshipPending = counts?.internshipPending ?? null;
  const fullAndFinalPending = counts?.fullAndFinalPending ?? null;

  const primaryItems = [
    { path: '/admin/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
  ];

  const menuSections = [
    {
      title: 'Employee Management',
      items: [
        { path: '/admin/onboarding', icon: UserPlus, label: 'Onboarding', badge: onboardingPending },
        { path: '/admin/employees', icon: Users, label: 'Employees List' },
        { path: '/admin/probation', icon: Hourglass, label: 'Probation Status', badge: probationPending },
        { path: '/admin/internship', icon: GraduationCap, label: 'Internship Status', badge: internshipPending },
        { path: '/admin/attendance', icon: Clock, label: 'Attendance' },
        { path: '/admin/attendance-requests', icon: ClipboardCheck, label: 'Regulation Request', badge: attendancePending },
        { path: '/admin/leaves', icon: Calendar, label: 'Leave Management', badge: leavePending },
        { path: '/admin/holidays', icon: CalendarDays, label: 'Holiday List' },
        { path: '/admin/birthdays', icon: Cake, label: 'Birthday List' },
        { path: '/admin/policies', icon: ScrollText, label: 'Company Policies' },
        { path: '/admin/wfh', icon: HomeIcon, label: 'WFH Requests', badge: wfhPending },
        { path: '/admin/comp-off', icon: Gift, label: 'Comp Off Requests', badge: compOffPending },
        { path: '/admin/full-and-final', icon: WalletCards, label: 'Full & Final Settlement', badge: fullAndFinalPending },
      ]
    },
    {
      title: 'Performance Review',
      items: [
        { path: '/admin/tasks', icon: CheckSquare, label: 'Task Management', badge: taskPending },
        { path: '/admin/performance', icon: BarChart3, label: 'Performance' },
        { path: '/admin/rating', icon: Star, label: 'Rating' },
      ]
    },
    {
      title: 'Payroll Management',
      items: [
        { path: '/admin/payroll', icon: Wallet, label: 'Payroll' },
        { path : '/admin/offer-letters', icon: FileText, label: 'Offer Letters', badge: offerLetterPending },
      ]
    }
  ];

  const renderMenuItem = (item) => {
    const Icon = item.icon;
    return (
      <li key={item.path}>
        <NavLink
          to={item.path}
          title={collapsed ? item.label : undefined}
          className={({ isActive }) =>
            `relative flex items-center py-2.5 text-sm font-medium rounded-xl transition-all duration-200 ${
              collapsed ? 'justify-center px-2' : 'px-4'
            } ${
              isActive
                ? 'bg-slate-200 text-slate-900 shadow-sm ring-1 ring-slate-300'
                : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
            }`
          }
          onClick={onClose}
        >
          <Icon className={collapsed ? 'h-5 w-5' : 'mr-3 h-5 w-5'} />
          {!collapsed && <span className="flex-1">{item.label}</span>}
          {!collapsed && item.badge != null && item.badge > 0 && (
            <span className="ml-2 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-xs font-bold bg-yellow-400 text-yellow-900">
              {item.badge > 99 ? '99+' : item.badge}
            </span>
          )}
          {collapsed && item.badge != null && item.badge > 0 && (
            <span className="absolute right-1.5 top-1.5 h-2 w-2 rounded-full bg-yellow-400" />
          )}
        </NavLink>
      </li>
    );
  };

  const toggleSection = (sectionTitle) => {
    setOpenSections((current) => ({
      ...current,
      [sectionTitle]: !current[sectionTitle]
    }));
  };

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 z-40 bg-gray-600 bg-opacity-75 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <div className={`
        fixed inset-y-0 left-0 z-50 flex flex-col overflow-hidden border-r border-slate-200 bg-slate-100 shadow-lg transform transition-all duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-0
        ${collapsed ? 'lg:w-20' : 'lg:w-64'} w-64
        ${isOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        <div className="border-b border-slate-200 bg-slate-50/90 px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex min-w-0 items-center gap-3">
              <div className="relative flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 shadow-sm">
                <Briefcase className="h-5 w-5 text-white" />
                <span className="absolute -right-1 -top-1 h-3 w-3 rounded-full bg-green-500 ring-2 ring-white" />
              </div>
              {!collapsed && (
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <h2 className="truncate text-base font-bold tracking-tight text-slate-900">PFS HRMS</h2>
                    <span className="shrink-0 rounded-md bg-indigo-100 px-1.5 py-0.5 text-[10px] font-bold text-indigo-600">v1.0</span>
                  </div>
                  {/* <p className="text-[10px] font-semibold uppercase tracking-[0.15em] text-slate-400">Enterprise Suite</p> */}
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={() => setCollapsed((current) => !current)}
              className="hidden shrink-0 rounded-md p-1.5 text-slate-400 transition-colors hover:bg-slate-200/70 hover:text-slate-700 lg:inline-flex"
            >
              <ChevronsLeft className={`h-5 w-5 transition-transform duration-300 ${collapsed ? 'rotate-180' : ''}`} />
            </button>
            <button
              onClick={onClose}
              className="lg:hidden rounded-md p-1 text-slate-400 hover:text-slate-700"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>

        <nav className={`mt-6 flex-1 overflow-y-auto pb-6 ${collapsed ? 'px-2' : 'px-4'}`}>
          <ul className="space-y-2">
            {primaryItems.map(renderMenuItem)}
          </ul>

          <div className="mt-5 space-y-5">
            {menuSections.map((section) => (
              <div key={section.title}>
                {!collapsed && (
                  <button
                    type="button"
                    onClick={() => toggleSection(section.title)}
                    className="mb-2 flex w-full items-center justify-between rounded-lg px-4 py-2 text-left text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500 transition-colors hover:bg-slate-200/70 hover:text-slate-700"
                  >
                    <span>{section.title}</span>
                    <ChevronDown
                      className={`h-4 w-4 transition-transform duration-200 ${openSections[section.title] ? 'rotate-180' : 'rotate-0'}`}
                    />
                  </button>
                )}
                {(collapsed || openSections[section.title]) && (
                  <ul className="space-y-2">
                    {section.items.map(renderMenuItem)}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </nav>
      </div>
    </>
  );
};

export default Sidebar;
